import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertCircle,
    Calendar,
    ChevronRight,
    Lock,
    Unlock,
    Users,
    Loader2,
    Mail,
    Eye,
    EyeOff,
    CheckCircle,
    Server
} from "lucide-react";
import AdminNavbar from "../../components/layout/AdminNavbar";
import api from "../../api/axios";
import { toast } from "react-hot-toast";

const AdminControlCenter = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lockUpdatingKey, setLockUpdatingKey] = useState("");

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await api.get("/api/admin/control-center");
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load admin control center.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = "Payroll Locking System | Savrion Technologies";
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const togglePayrollMonthLock = async (period) => {
        if (!period.periodKey || lockUpdatingKey) return;
        try {
            setLockUpdatingKey(period.periodKey);
            await api.patch(`/api/admin/payroll/month-locks/${period.periodKey}`, {
                locked: !period.monthLocked,
            });
            toast.success(`Payroll month ${period.payMonth} ${!period.monthLocked ? 'locked' : 'unlocked'} successfully`);
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update payroll month lock.");
        } finally {
            setLockUpdatingKey("");
        }
    };

    const formatMonthDisplay = (key) => {
        if (!key) return "";
        const [year, month] = key.split('-');
        const date = new Date(year, parseInt(month) - 1, 1);
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    // Derived stats for Current Month
    const currentPeriod = data?.payrollLocks?.[0];
    const totalEmployees = data?.todayStatus?.activeEmployees || 0;
    const currentLocked = currentPeriod?.locked || 0;
    const currentUnlocked = currentPeriod?.unlocked || 0;
    const currentMissing = currentPeriod?.missing || 0;
    const currentTotal = currentLocked + currentUnlocked + currentMissing;
    
    // Percentage formatting
    const getPercent = (val, total) => total > 0 ? ((val / total) * 100).toFixed(2) : 0;
    const lockedPercent = getPercent(currentLocked, currentTotal || totalEmployees);
    const unlockedPercent = getPercent(currentUnlocked, currentTotal || totalEmployees);
    const missingPercent = getPercent(currentMissing, currentTotal || totalEmployees);

    // Circular Progress Component
    const CircularProgress = ({ percentage }) => {
        const radius = 36;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (percentage / 100) * circumference;
        return (
            <div className="relative flex items-center justify-center w-24 h-24">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                        className="text-gray-200 dark:text-neutral-800"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="50"
                        cy="50"
                    />
                    <circle
                        className="text-[var(--accent-primary)] transition-all duration-1000 ease-in-out"
                        strokeWidth="8"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="50"
                        cy="50"
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-sm font-bold text-[var(--text-primary)]">{percentage}%</span>
                    <span className="text-[9px] font-medium text-[var(--text-secondary)]">Locked</span>
                </div>
            </div>
        );
    };

    return (
        <div className="flex min-h-screen flex-col xl:flex-row bg-[var(--bg-primary)] text-[var(--text-primary)] font-sans">
            <AdminNavbar />
            <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 xl:p-10">
                {/* Header Area */}
                <div className="flex flex-col gap-1 mb-8">
                    <h3 className="text-[var(--warning)] text-xs font-bold uppercase tracking-wider">Payroll Locking System</h3>
                    <h1 className="text-3xl font-bold text-[var(--text-primary)] tracking-tight">Payroll Lock Dashboard</h1>
                    <p className="text-[var(--text-secondary)] text-sm mt-1">Manage payroll month locks and employee lock status.</p>
                </div>

                {loading && !data ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-primary)]" />
                    </div>
                ) : data && (
                    <div className="flex flex-col gap-8 max-w-7xl">
                        
                        {/* 4 Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Total Employees */}
                            <div className="bg-[var(--card-bg)] rounded-2xl p-5 border border-[var(--border-color)] shadow-sm flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-[#f3efff] dark:bg-[var(--accent-primary)]/10 flex items-center justify-center text-[var(--accent-primary)] shrink-0">
                                    <Users size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)] mb-0.5">Total Employees</p>
                                    <p className="text-2xl font-bold text-[var(--text-primary)] leading-none">{totalEmployees}</p>
                                    <p className="text-[10px] text-slate-400 mt-1">Across all months</p>
                                </div>
                            </div>
                            {/* Locked */}
                            <div className="bg-[var(--card-bg)] rounded-2xl p-5 border border-[var(--border-color)] shadow-sm flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
                                    <Lock size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)] mb-0.5">Locked</p>
                                    <p className="text-2xl font-bold text-[var(--text-primary)] leading-none">{currentLocked}</p>
                                    <p className="text-[10px] text-emerald-500 font-semibold mt-1">{lockedPercent}% of total</p>
                                </div>
                            </div>
                            {/* Unlocked */}
                            <div className="bg-[var(--card-bg)] rounded-2xl p-5 border border-[var(--border-color)] shadow-sm flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center text-orange-500 shrink-0">
                                    <Unlock size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)] mb-0.5">Unlocked</p>
                                    <p className="text-2xl font-bold text-[var(--text-primary)] leading-none">{currentUnlocked}</p>
                                    <p className="text-[10px] text-orange-500 font-semibold mt-1">{unlockedPercent}% of total</p>
                                </div>
                            </div>
                            {/* Missing */}
                            <div className="bg-[var(--card-bg)] rounded-2xl p-5 border border-[var(--border-color)] shadow-sm flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center text-red-500 shrink-0">
                                    <AlertCircle size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)] mb-0.5">Missing</p>
                                    <p className="text-2xl font-bold text-[var(--text-primary)] leading-none">{currentMissing}</p>
                                    <p className="text-[10px] text-red-500 font-semibold mt-1">{missingPercent}% of total</p>
                                </div>
                            </div>
                        </div>

                        {/* Payroll Months Section */}
                        <div className="flex flex-col gap-4 mt-2">
                            <div className="flex items-center gap-2">
                                <Calendar className="text-[var(--accent-primary)]" size={20} />
                                <h2 className="text-lg font-bold text-[var(--text-primary)]">Payroll Months</h2>
                            </div>

                            {/* Current Month High-fidelity Card */}
                            {currentPeriod && (
                                <div className="bg-[var(--card-bg)] rounded-2xl border-2 border-[var(--accent-primary)]/30 dark:border-[var(--accent-primary)]/40 shadow-md shadow-[var(--accent-primary)]/5 overflow-hidden flex flex-col md:flex-row relative">
                                    {/* Left side: Month info */}
                                    <div className="p-6 md:p-8 flex flex-col justify-center min-w-[200px]">
                                        <div className="inline-block bg-[#f3efff] dark:bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] text-[10px] font-bold px-3 py-1 rounded-full w-max mb-3">CURRENT MONTH</div>
                                        <h3 className="text-xl font-bold text-[var(--text-primary)] mb-1">{formatMonthDisplay(currentPeriod.periodKey)}</h3>
                                        <p className="text-sm text-[var(--text-secondary)] font-medium">{currentPeriod.periodKey}</p>
                                    </div>
                                    
                                    {/* Middle: Stats & Progress */}
                                    <div className="flex-1 flex flex-wrap items-center justify-between p-6 md:p-8 border-t md:border-t-0 md:border-l border-[var(--border-color)]">
                                        <div className="flex gap-8 md:gap-16">
                                            <div className="flex flex-col items-center">
                                                <span className="text-2xl font-bold text-[var(--accent-primary)] mb-1">{currentPeriod.locked}</span>
                                                <span className="text-xs text-[var(--text-secondary)] font-medium mb-1.5">Locked</span>
                                                <Users size={16} className="text-[var(--accent-primary)]" />
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="text-2xl font-bold text-orange-500 mb-1">{currentPeriod.unlocked}</span>
                                                <span className="text-xs text-[var(--text-secondary)] font-medium mb-1.5">Unlocked</span>
                                                <Unlock size={16} className="text-orange-500" />
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="text-2xl font-bold text-red-500 mb-1">{currentPeriod.missing}</span>
                                                <span className="text-xs text-[var(--text-secondary)] font-medium mb-1.5">Missing</span>
                                                <AlertCircle size={16} className="text-red-500" />
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 mt-6 md:mt-0 pl-4 md:pl-8 md:border-l border-[var(--border-color)]">
                                            <CircularProgress percentage={lockedPercent} />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-[var(--text-primary)]">{currentPeriod.locked} of {totalEmployees}</span>
                                                <span className="text-xs text-[var(--text-secondary)]">employees locked</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="p-6 md:p-8 flex flex-col justify-center gap-3 border-t md:border-t-0 md:border-l border-[var(--border-color)] min-w-[220px]">
                                        <button
                                            onClick={() => togglePayrollMonthLock(currentPeriod)}
                                            disabled={lockUpdatingKey === currentPeriod.periodKey}
                                            className={`w-full py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-bold transition-all shadow-sm ${
                                                currentPeriod.monthLocked 
                                                    ? "bg-emerald-500 hover:bg-emerald-600 text-white" 
                                                    : "bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white"
                                            }`}
                                        >
                                            {lockUpdatingKey === currentPeriod.periodKey ? (
                                                <Loader2 size={16} className="animate-spin" />
                                            ) : (
                                                currentPeriod.monthLocked ? <Unlock size={16} /> : <Lock size={16} />
                                            )}
                                            {currentPeriod.monthLocked ? "Unlock Month" : "Lock Month"}
                                        </button>
                                        <button 
                                            onClick={() => navigate("/admin/payroll")}
                                            className="w-full py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] hover:bg-[var(--bg-secondary)] flex items-center justify-center gap-2 text-sm font-bold text-[var(--text-primary)] transition-all"
                                        >
                                            View Employees <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Table of previous months */}
                            <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--border-color)] overflow-hidden mt-4 shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse min-w-[800px]">
                                        <thead className="bg-[var(--bg-secondary)]">
                                            <tr>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)] w-[20%]">Month</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)] w-[15%]">Period</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)] flex items-center gap-1.5"><Users size={14} className="text-[var(--accent-primary)]"/> Locked</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)]"><Unlock size={14} className="text-orange-500 inline mr-1.5" /> Unlocked</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)]"><AlertCircle size={14} className="text-red-500 inline mr-1.5" /> Missing</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)]">Status</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-[var(--text-secondary)] text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                                            {data.payrollLocks.slice(1).map((period) => (
                                                <tr key={period.periodKey} className="hover:bg-[var(--bg-secondary)] transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center text-orange-500 shrink-0">
                                                                <Calendar size={14} />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-bold text-[var(--text-primary)]">{formatMonthDisplay(period.periodKey)}</p>
                                                                <p className="text-[10px] text-[var(--text-secondary)]">{period.periodKey}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="text-xs text-[var(--text-secondary)]">{period.periodKey}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-bold text-[var(--accent-primary)]">{period.locked}</td>
                                                    <td className="px-6 py-4 text-sm font-bold text-orange-500">{period.unlocked}</td>
                                                    <td className="px-6 py-4 text-sm font-bold text-red-500">{period.missing}</td>
                                                    <td className="px-6 py-4">
                                                        {period.monthLocked ? (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold border border-emerald-100 dark:border-emerald-900/50">
                                                                <Lock size={10} /> Locked
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold border border-slate-200 dark:border-slate-700">
                                                                <Unlock size={10} /> Unlocked
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button 
                                                            onClick={() => navigate("/admin/payroll")}
                                                            className="inline-flex items-center justify-center gap-1 px-4 py-1.5 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] hover:bg-[var(--bg-secondary)] text-xs font-bold text-[var(--text-primary)] transition-all"
                                                        >
                                                            View Employees <ChevronRight size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {data.payrollLocks.length <= 1 && (
                                                <tr>
                                                    <td colSpan="7" className="px-6 py-8 text-center text-sm text-[var(--text-secondary)]">
                                                        No previous months found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>
                )}

                {/* Email Configuration Moved to Company Settings */}

            </main>
        </div>
    );
};

export default AdminControlCenter;

